'use client';

import {
  Component,
  type ComponentType,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import * as React from 'react'; // https://react.dev/reference/react/captureOwnerStack#captureownerstack-is-not-available

import { parseError } from '@infinex-private/ts-utils/error';

import type { ErrorBoundaryPayload } from './types';
import { ERROR_BOUNDARY_CUSTOM_EVENT_TYPE } from './types';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional additional details to be included in the error payload */
  details?: Record<string, unknown>;
  /** Fallback to render when an error has occurred */
  fallback?: ComponentType<ErrorBoundaryFallbackProps>;
  /** Is fatal for the application */
  isFatal?: boolean;
  /** Name of the boundary for identification */
  name: string;
  /** Optional callback when an error is caught, ex. to dismiss a modal or a loading state */
  onError?: (payload: ErrorBoundaryPayload) => void;
}

export interface ErrorBoundaryFallbackProps {
  error: Error;
  reset: () => void;
}

type State =
  | {
      didCatch: true;
      error: Error;
    }
  | {
      didCatch: false;
      error: null;
    };

/**
 * Basic error boundary component.
 * - Catches JavaScript errors anywhere in its child component tree
 * - logs those errors
 * - displays a fallback UI instead through a render prop
 * - You can pass additional details to be included in the error payload
 * - You can listen to errors globally = useful for reporting to external services such as Datadog
 *
 * https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      didCatch: false as const,
      error: null,
    };
  }

  /**
   * https://react.dev/reference/react/Component#static-getderivedstatefromerror
   */
  public static getDerivedStateFromError(error: Error) {
    return { didCatch: true, error };
  }

  /**
   * https://react.dev/reference/react/Component#componentdidcatch
   */
  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const details = this.props.details || {};
    const payload: ErrorBoundaryPayload = {
      error,
      errorInfo,
      ownerStack: React.captureOwnerStack?.() ?? undefined, // conditional export https://react.dev/reference/react/captureOwnerStack#captureownerstack-is-not-available
      context: {
        ...details,
        isFatal: !!this.props.isFatal,
        name: this.props.name,
      },
    };

    // So that we can ask customers to copy-paste / screenshot the error from the console
    console.group('%c---------------------', 'color:red;');
    console.log('%cSomething went wrong!', 'color:red; font-size: large;');
    console.log(`Error caught in <ui.ErrorBoundary /> "${this.props.name}"`);
    console.log('Message:', parseError(error));
    console.log('Time:', Date.now());
    console.log(error);
    console.log(errorInfo);
    console.log(details);
    console.log('%c---------------------', 'color:red;');
    console.groupEnd();

    this.props.onError?.(payload);

    window.dispatchEvent(
      new CustomEvent(ERROR_BOUNDARY_CUSTOM_EVENT_TYPE, {
        detail: payload,
      })
    );
  }

  override render() {
    if (this.state.didCatch) {
      const Fallback = this.props.fallback;
      return Fallback
        ? React.createElement(Fallback, {
            error: this.state.error,
            reset: this.reset,
          })
        : null;
    }

    return this.props.children;
  }

  private reset = () => {
    this.setState({
      didCatch: false,
      error: null,
    });
  };
}
